/**
 * Edit dialog for an existing webhook's basic config.
 *
 * Scope is deliberately narrow - description, endpoint URL, max_attempts,
 * is_active. Re-selecting events lives on the Events tab.
 *
 * Form driven by generated useForm per the codebase's "hand-rolled forms
 * are banned" rule (CLAUDE.md). The footer Save button submits via the
 * HTML `form` attribute so we can keep DialogFooter ownership of the
 * Cancel/Save layout.
 */

import { useMemo } from 'react'
import { Loader2 } from 'lucide-react'

import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'
import { toastApiError } from '@/shared/lib/api-error'
import { toast } from 'sonner'

import type { UpdateWebhookInput } from '@/admin/hooks/use-pulse-webhooks'
import type { PulseWebhookSubscription } from '@/admin/types/pulse'

import { buildAuthPayload, buildWebhookAuthFields } from './webhook-auth-fields'

const FORM_ID = 'edit-webhook-form'

export interface EditWebhookDialogProps {
  open: boolean
  webhook: PulseWebhookSubscription
  isPending: boolean
  onClose: () => void
  onSave: (changes: UpdateWebhookInput) => Promise<unknown>
}

export function EditWebhookDialog({
  open,
  webhook,
  isPending,
  onClose,
  onSave,
}: EditWebhookDialogProps) {
  // Render the form inside the dialog only while it's open. Mounting
  // fresh on every open seeds state from the current webhook snapshot
  // without needing a useEffect to re-sync.
  return (
    <Dialog open={open} onOpenChange={(o) => (o ? null : onClose())}>
      <DialogContent className="sm:max-w-lg">
        {open && (
          <EditForm
            webhook={webhook}
            isPending={isPending}
            onClose={onClose}
            onSave={onSave}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

function EditForm({
  webhook,
  isPending,
  onClose,
  onSave,
}: Omit<EditWebhookDialogProps, 'open'>) {
  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'text',
        name: 'description',
        label: 'Description',
        placeholder: 'Production claims pipeline',
        value: webhook.description ?? '',
      },
      {
        type: 'text',
        name: 'target_url',
        label: 'Endpoint URL',
        placeholder: 'https://api.your-system.com/hooks/iwarranty',
        value: webhook.target_url,
        required: 'Endpoint URL is required.',
        validate: (raw) => {
          const v = typeof raw === 'string' ? raw.trim() : ''
          if (!v) return 'Endpoint URL is required.'
          if (!/^https:\/\//i.test(v)) return 'Must be HTTPS.'
          return undefined
        },
      },
      {
        type: 'number',
        name: 'max_attempts',
        label: 'Max delivery attempts',
        value: webhook.max_attempts,
        helperText:
          'Backoff steps: 0s -> 30s -> 2m -> 10m -> 1h -> 6h -> 24h.',
        validate: (raw) => {
          const n = Number(raw)
          if (!Number.isInteger(n) || n < 1 || n > 7)
            return 'Must be between 1 and 7.'
          return undefined
        },
      },
      ...buildWebhookAuthFields(webhook.auth),
      {
        type: 'checkbox',
        name: 'is_active',
        label: 'Webhook is active and will receive events',
        value: webhook.is_active,
      },
    ],
    [webhook],
  )

  const { Form: EditForm } = useForm(
    {
      name: FORM_ID,
      // 'footer' suppresses the inline submit row - we render our own
      // Save button inside DialogFooter and trigger it via the HTML
      // `form` attribute on the button.
      submit: 'footer',
      fields,
    },
    async (values, formCtx) => {
      const v = values as {
        description: string
        target_url: string
        max_attempts: number | string
        is_active: boolean
      }
      // `auth` is only included when the operator changed the auth
      // section - omitting it keeps the stored credentials intact.
      const { auth, error: authError } = buildAuthPayload(values, webhook.auth)
      if (authError) {
        formCtx.setFieldError(authError.field, authError.message)
        return
      }
      try {
        await onSave({
          description: (v.description ?? '').trim(),
          target_url: v.target_url.trim(),
          max_attempts: Number(v.max_attempts),
          is_active: Boolean(v.is_active),
          enabled_events: webhook.enabled_events,
          ...(auth ? { auth } : {}),
        })
        toast.success('Webhook updated.')
        onClose()
      } catch (err) {
        toastApiError(err, 'Failed to update webhook.')
      }
    },
    [webhook],
  )

  return (
    <>
      <DialogHeader>
        <DialogTitle>Edit webhook</DialogTitle>
        <DialogDescription>
          Update the endpoint, retry budget, or human label. Event
          subscriptions stay editable from the Events tab.
        </DialogDescription>
      </DialogHeader>

      <div className="py-2">
        <EditForm className="flex flex-col gap-4" />
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={isPending}>
          Cancel
        </Button>
        <Button type="submit" form={FORM_ID} disabled={isPending}>
          {isPending && <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />}
          Save changes
        </Button>
      </DialogFooter>
    </>
  )
}
